// modules/analytics/index.ts — Platik-IA
import { bus }            from '../../core/event-bus';
import { getModule }      from '../../core/registry';
import { getPeriodDates } from '../../shared/utils/format';
import type { AnalyticsAPI }                       from './contracts';
import type { Period, FinancialSummary, ChartDataPoint, Transaction } from '../../shared/types/global';

function getTxs(): Transaction[] {
  return getModule<{ getAll: () => Transaction[] }>('transactions')?.getAll() ?? [];
}

function filterByPeriod(txs: Transaction[], period: Period): Transaction[] {
  const { desde, hasta } = getPeriodDates(period);
  return txs.filter(t => {
    const d = new Date(t.fecha);
    return d >= desde && d <= hasta;
  });
}

export async function initAnalytics(): Promise<AnalyticsAPI> {
  bus.emit('analytics:ready');

  return {
    getSummary(period: Period): FinancialSummary {
      const txs      = filterByPeriod(getTxs(), period);
      let ingresos   = 0;
      let gastos     = 0;
      const porCat:  Record<string, number> = {};
      const porMes:  Record<string, { ingresos: number; gastos: number }> = {};

      for (const tx of txs) {
        const mesKey = tx.fecha.slice(0, 7); // YYYY-MM
        if (!porMes[mesKey]) porMes[mesKey] = { ingresos: 0, gastos: 0 };

        if (tx.tipo === 'Ingreso') {
          ingresos += tx.monto;
          porMes[mesKey].ingresos += tx.monto;
        } else {
          gastos += tx.monto;
          porMes[mesKey].gastos += tx.monto;
          porCat[tx.categoria] = (porCat[tx.categoria] ?? 0) + tx.monto;
        }
      }

      const saldo      = ingresos - gastos;
      const tasaAhorro = ingresos > 0 ? (saldo / ingresos) * 100 : 0;
      const topEntry   = Object.entries(porCat).sort((a, b) => b[1] - a[1])[0];

      return {
        ingresos,
        gastos,
        saldo,
        tasaAhorro,
        topCategoria: topEntry ? { nombre: topEntry[0], monto: topEntry[1] } : null,
        tendencia: Object.entries(porMes).sort().map(([mes, v]) => ({ mes, ...v })),
      };
    },

    getDistribution(period: Period, tipo: 'Gasto' | 'Ingreso'): ChartDataPoint[] {
      const txs = filterByPeriod(getTxs(), period).filter(t => t.tipo === tipo);
      const acum: Record<string, number> = {};
      for (const tx of txs) {
        acum[tx.categoria] = (acum[tx.categoria] ?? 0) + tx.monto;
      }
      return Object.entries(acum)
        .map(([nombre, total]) => ({ nombre, total }))
        .filter(d => d.total > 0)
        .sort((a, b) => b.total - a.total);
    },

    dispose() {},
  };
}
